import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Close } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Grid,
  Chip,
  Stack,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  FormControl,
} from '@mui/material';

import { getAllCampaign } from 'src/server/store/campaign';

const STATUS_COLOR = {
  Enabled: 'info',
  Paused: 'warning',
};

const getCampaignTypeLabel = (type) => {
  switch (type) {
    case 'SPONSORED_PRODUCT':
      return 'SP';
    case 'SPONSORED_BRAND':
      return 'SB';
    case 'SPONSORED_DISPLAY':
      return 'SD';
    default:
      return '';
  }
};

// TODO :
// 1. campaign search and pagination done
// 2. add all campaigns done
// 3. remove all campaigns done
// 4. when create automation > custom criteria (not from the dropdown) > create criteria first then pass the id to the automation create
// 5. save automation template done

function CampaignSelectionCard({ value, onChange, setValue, watch }) {
  const dispatch = useDispatch();
  const { campaignList } = useSelector((state) => ({
    campaignList: state.campaigns?.allCampaignLists || [],
  }));

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    const credentials = {
      page,
      limit,
      search,
      dispatch,
    };
    dispatch(getAllCampaign(credentials));
  }, [dispatch, page, limit, search]);

  const campaigns = campaignList?.campaigns || [];

  // Filtered and paginated campaigns
  const filtered = campaigns || [];
  const totalPages = Math.ceil(campaignList.total / limit) || 1;

  const handleToggle = (campaignId) => {
    if (value.includes(campaignId)) {
      onChange(value.filter((v) => v !== campaignId));
    } else {
      onChange([...value, campaignId]);
    }
  };

  const campaignOptions = watch('campaignOptions');

  const handleAddAll = () => {
    setValue('campaignOptions', 'ALL');
  };

  const handleRemoveAll = () => {
    onChange([]);
    setValue('campaignOptions', 'CUSTOM');
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  return (
    <Grid container spacing={2} mt={0}>
      <Grid item xs={6}>
        <Box border={1} borderColor="grey.300" borderRadius={2} p={2}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <TextField
              placeholder="Search campaigns"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              size="small"
              fullWidth
            />
            <Button onClick={handleAddAll} variant="outlined" size="small">
              Add All
            </Button>
          </Stack>
          <Box maxHeight={250} overflow="auto" className="my-custom-scrollbar">
            {campaigns?.map((c) => (
              <Stack
                key={c.campaignId}
                direction="row"
                alignItems="center"
                spacing={1}
                mb={1}
                sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}
              >
                {c.campaignType && (
                  <Chip
                    label={getCampaignTypeLabel(c.campaignType)}
                    color="default"
                    size="small"
                    variant="outlined"
                  />
                )}
                <Typography variant="overline" sx={{ flex: 1 }}>
                  {c.name}
                </Typography>
                {/* Status chip */}
                <Chip
                  label={c.state}
                  color={STATUS_COLOR[c.state]}
                  size="small"
                  sx={{
                    fontSize: '12px',
                    minWidth: 70,
                    backgroundColor: c.state === 'Enabled' ? '#E3F2FD' : '#FFF8E1',
                    color: c.state === 'Enabled' ? '#1976d2' : '#b28704',
                    '&:hover': {
                      backgroundColor: c.state === 'Enabled' ? '#E3F2FD' : '#FFF8E1',
                      color: c.state === 'Enabled' ? '#1976d2' : '#b28704',
                    },
                  }}
                />
                {/* Add button */}
                <IconButton
                  color={value.includes(c.campaignId) ? 'default' : 'info'}
                  onClick={() => handleToggle(c.campaignId)}
                  disabled={value.includes(c.campaignId)}
                  sx={{
                    py: 0.3,
                    px: 1,
                    fontSize: '12px',
                    ml: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 0.7,
                  }}
                  size="small"
                >
                  {value.includes(c.campaignId) ? (
                    <CheckIcon fontSize="small" sx={{ fontSize: '12px' }} />
                  ) : (
                    <AddIcon fontSize="small" sx={{ fontSize: '16px' }} />
                  )}{' '}
                  &nbsp;{value.includes(c.campaignId) ? 'Added' : 'Add'}
                </IconButton>
              </Stack>
            ))}
            {campaigns.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                No campaigns found.
              </Typography>
            )}
          </Box>
          {/* Pagination and limit */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="small"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <ArrowBackIosNewIcon fontSize="small" sx={{ fontSize: '16px' }} />
              </IconButton>
              <Typography variant="body2">{page}</Typography>
              <Typography variant="body2">/</Typography>
              <Typography variant="body2">{totalPages}</Typography>
              <IconButton
                size="small"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                <ArrowForwardIosIcon fontSize="small" sx={{ fontSize: '16px' }} />
              </IconButton>
            </Stack>

            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                sx={{ padding: '0px' }}
                size="small"
                value={limit}
                onChange={handleLimitChange}
              >
                {[2, 10, 25, 50].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box border={1} borderColor="grey.300" borderRadius={2} p={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle2">
              {campaignOptions === 'ALL' ? campaignList.total : value.length} Campaigns Added
            </Typography>
            <Button onClick={handleRemoveAll} variant="outlined" size="small">
              Remove All
            </Button>
          </Stack>
          {campaignOptions === 'ALL' ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                backgroundColor: '#E6F9EC', // light green
                borderRadius: 2,
                py: 2,
                px: 2,
                mb: 1,
              }}
            >
              <CheckIcon sx={{ color: '#43A047', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                All campaigns added.
              </Typography>
            </Box>
          ) : (
            <Box maxHeight={310} overflow="auto" className="my-custom-scrollbar">
              {(() => {
                let content;
                if (value.length > 0) {
                  content = filtered
                    .filter((c) => value.includes(c.campaignId))
                    .map((c) => (
                      <Stack
                        key={c.campaignId}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        mb={1}
                        sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}
                      >
                        <Chip
                          label={getCampaignTypeLabel(c.campaignType)}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="overline" sx={{ flex: 1 }}>
                          {c.name}
                        </Typography>
                        {/* Status chip */}
                        <Chip
                          label={c.state}
                          color={STATUS_COLOR[c.state]}
                          size="small"
                          sx={{
                            fontSize: '12px',
                            minWidth: 70,
                            backgroundColor: c.state === 'Enabled' ? '#E3F2FD' : '#FFF8E1',
                            color: c.state === 'Enabled' ? '#1976d2' : '#b28704',
                            '&:hover': {
                              backgroundColor: c.state === 'Enabled' ? '#E3F2FD' : '#FFF8E1',
                              color: c.state === 'Enabled' ? '#1976d2' : '#b28704',
                            },
                          }}
                        />
                        <IconButton size="small" onClick={() => handleToggle(c.campaignId)}>
                          <Close fontSize="small" sx={{ fontSize: '16px' }} />
                        </IconButton>
                      </Stack>
                    ));
                } else {
                  content = (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 2 }}
                    >
                      No campaigns added.
                    </Typography>
                  );
                }
                return content;
              })()}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}

CampaignSelectionCard.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
};

export default CampaignSelectionCard;
